import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Inject,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { Store } from '@ngxs/store';
import {
  OrderApi,
  BookingApi,
  CashDrawerApi,
  PaymentApi,
  OrderDTO,
  OrderItemDTO,
  PaymentDTO,
  PaymentInvoiceDTO,
  CashDrawerActionDTOCashDrawerActionTypeEnum,
  PaymentMethodPaymentMethodIdEnum,
  PaymentDTOTransactionTypeEnum,
} from '@lkp/parking-sdk';
import {
  ORDER_API,
  BOOKING_API,
  CASH_DRAWER_API,
  PAYMENT_API,
} from '../../core/providers/api.module';
import { Subject, first, takeUntil } from 'rxjs';
import { ConfirmDialogComponent } from 'src/app/components/confirm-dialog/confirm-dialog.component';
import { ErrorDialogComponent } from 'src/app/components/error-dialog/error-dialog.component';
import { CashPaymentComponent } from 'src/app/components/payment/cash-payment/cash-payment.component';
import { PaxPaymentComponent } from 'src/app/components/payment/pax-payment/pax-payment.component';
import { ZeroPaymentComponent } from 'src/app/components/payment/zero-payment/zero-payment.component';
import { DateService } from 'src/app/core/services/date.service';
import { GetBooking } from 'src/app/core/states/bookings/booking.actions';
import {
  CheckoutOrderCash,
  CheckoutOrderPayLater,
  DeleteOrder,
  GetOrder,
} from 'src/app/core/states/orders/orders.action';
import { OrdersState } from 'src/app/core/states/orders/orders.state';
import { InvoiceFormFieldsComponent } from 'src/app/components/invoice-form-fields/invoice-form-fields.component';

@Component({
    selector: 'app-checkout',
    templateUrl: './checkout.component.html',
    styleUrls: ['./checkout.component.scss'],
    standalone: false
})
export class CheckoutComponent implements OnDestroy {
  @HostListener('window:beforeunload', ['$event'])
  confirmReload(event: BeforeUnloadEvent) {
    event.preventDefault();
  }

  @ViewChild('invoiceFields') invoiceFields?: InvoiceFormFieldsComponent;

  @ViewChild('iframe', { read: ElementRef, static: false }) iframe:
    | ElementRef
    | undefined;

  refund = false;
  allowPayments = true;

  currentOrder: OrderDTO = {};
  paymentMethod: FormGroup;
  invoice?: PaymentInvoiceDTO;
  zeroCheckout = false;
  invalidFormMessage: string = '';
  startedPayments?: PaymentDTO[];

  newOrder = true; // Listened to by checkout guard
  paymentFinished = false; // Listened to by checkout guard
  noReset = false; // Listened to by checkout guard

  loading = false;
  emailSent = false;
  emailMsg?: string;

  childFormValid = false;

  /* Forms */
  discountForm?: FormGroup;
  discountedItem?: OrderItemDTO;

  readonly onDestroy$ = new Subject<void>();

  constructor(
    readonly store: Store,
    readonly dialog: MatDialog,
    readonly router: Router,
    readonly snackBar: MatSnackBar,
    readonly formBuilder: FormBuilder,
    @Inject(ORDER_API) readonly orderApi: OrderApi,
    @Inject(BOOKING_API) readonly bookingApi: BookingApi,
    @Inject(CASH_DRAWER_API) readonly cashDrawerApi: CashDrawerApi,
    @Inject(PAYMENT_API) readonly paymentApi: PaymentApi,
    readonly cdr: ChangeDetectorRef,
    readonly dateService: DateService
  ) {
    this.store
      .select(OrdersState.order)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((order) => {
        this.currentOrder = order;
      });
    this.paymentMethod = this.formBuilder.group({
      method: new FormControl<string | undefined>(undefined),
    });
    this.store
      .selectOnce(OrdersState.newOrder)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((newOrder) => {
        this.newOrder = newOrder; // This is always true..
      });

    this.discountForm = this.formBuilder.group({
      discountAmount: new FormControl<number | undefined>(undefined, [
        Validators.required,
      ]),
      discountType: new FormControl<string>(
        this.currentOrder.discountType === 'PERCENT' ? '%' : 'kr',
        [Validators.required]
      ),
      discountComment: new FormControl<string | undefined>(undefined, [
        Validators.required,
      ]),
    });
  }

  ngOnInit() {
    this.reloadOrder();
    this.getUnfinishedPayments();
    if (
      this.currentOrder?.amountToPay !== undefined &&
      this.currentOrder.amountToPay < 0
    )
      this.refund = true;

    this.paymentMethod.valueChanges
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(() => {
        this.invalidFormMessage = '';
      });
    this.discountForm?.valueChanges
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(() => {
        this.invalidFormMessage = '';
      });
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  getUnfinishedPayments() {
    this.currentOrder.orderId &&
      this.orderApi
        .getUnfinishedPayments({ orderId: this.currentOrder.orderId })
        .pipe(first())
        .subscribe((response) => {
          if (response?.length) {
            this.startedPayments = response;
            this.allowPayments = false;
          } else {
            this.startedPayments = undefined;
            this.allowPayments = true;
          }
        });
  }

  reloadOrder(): void {
    this.store
      .dispatch(new GetOrder(this.currentOrder.orderId!))
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(() => {
        if (this.currentOrder.amountToPay! <= 0) {
          this.paymentFinished = false;
          this.zeroCheckout = true;
        } else {
          this.zeroCheckout = false;
        }
      });
  }

  containsOnlyPrepaid(): boolean {
    let result = true;
    this.currentOrder.orderItems?.forEach((item) => {
      if (item.booking || item.feature) {
        result = false;
      }
    });
    return result;
  }

  setDiscountItem(item: OrderItemDTO) {
    if (this.discountedItem === item) {
      this.discountedItem = undefined;
    } else {
      this.discountedItem = item;
      this.discountForm?.controls['discountAmount'].setValue(
        item.discount || undefined
      );
      this.discountForm?.controls['discountComment'].setValue(
        item.discountComment || undefined
      );
    }
  }

  applyDiscount() {
    const item = this.currentOrder.orderItems?.find(
      (object) => object === this.discountedItem
    )!;
    item.discount = this.discountForm?.controls['discountAmount'].value;
    item.discountComment = this.discountForm?.controls['discountComment'].value;
    this.orderApi
      .updateOrderItemDiscountAdmin({
        orderId: this.currentOrder.orderId!,
        orderItemId: item.orderItemId!,
        orderItemDTO: item,
      })
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((order) => {
        if (order?.amountToPay !== undefined && order.amountToPay === 0)
          this.zeroCheckout = true;
        else this.zeroCheckout = false;

        if (order?.amountToPay !== undefined && order.amountToPay < 0)
          this.refund = true;
        else this.refund = false;

        // Update payment methods based on new amount
        this.reloadOrder();
      });
    this.discountedItem = undefined;
  }

  removeDiscount(item: OrderItemDTO) {
    item.discount = 0;
    item.discountComment = undefined;

    this.orderApi
      .updateOrderItemDiscountAdmin({
        orderId: this.currentOrder.orderId!,
        orderItemId: item.orderItemId!,
        orderItemDTO: item,
      })
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((order) => {
        if (order?.amountToPay !== undefined && order.amountToPay > 0)
          this.zeroCheckout = false;
        else this.zeroCheckout = true;

        if (order?.amountToPay !== undefined && order.amountToPay < 0)
          this.refund = true;
        else this.refund = false;

        // Update payment methods based on new amount
        this.reloadOrder();
      });
  }

  awaitReceiptChanges(): Promise<void> {
    return new Promise<void>((resolve) => {
      this.cdr.detectChanges();
      setTimeout(() => {
        resolve();
      }, 0);
    });
  }

  printReceipt() {
    this.cashDrawerApi
      .addCashDrawerActionAdmin({
        cashDrawerActionDTO: {
          cashDrawerActionType:
            CashDrawerActionDTOCashDrawerActionTypeEnum.Payment,
          comment: 'Payment',
        },
      })
      .pipe(takeUntil(this.onDestroy$))
      .subscribe();

    let latestPayment =
      (this.currentOrder?.payments?.length &&
        this.currentOrder.payments[this.currentOrder.payments.length - 1]) ||
      undefined;

    latestPayment?.paymentId &&
      this.paymentApi
        .getPaymentReceiptHtmlAdmin({ paymentId: latestPayment.paymentId })
        .pipe(takeUntil(this.onDestroy$))
        .subscribe({
          next: (v) => {
            if (v.html) {
              if (this.iframe) {
                this.iframe.nativeElement.contentWindow.document.open();
                this.iframe.nativeElement.contentWindow.document.close();
                this.iframe.nativeElement.contentWindow.frames.document.body.insertAdjacentHTML(
                  'afterbegin',
                  v.html
                );
                this.awaitReceiptChanges().then(() => {
                  this.iframe?.nativeElement.contentWindow.print();
                });
              }
            }
          },
          error: (error) => {
            console.error('Error fetching receipt: ', error);

            this.dialog.open(ErrorDialogComponent, {
              data: {
                title: 'Det gick inte att skriva ut kvittot',
                message: ['Kvittot kunde inte hittas i kassasystemet.'],
              },
            });
          },
        });
  }

  handlePayment() {
    this.invalidFormMessage = '';
    let order = this.currentOrder;

    if (this.zeroCheckout) {
      let dialogRef = this.dialog.open(ZeroPaymentComponent, {
        width: '30vw',
        data: { order },
      });

      dialogRef
        .afterClosed()
        .pipe(takeUntil(this.onDestroy$))
        .subscribe((result) => {
          if (result.success) {
            this.paymentFinished = true;

            this.store
              .dispatch(new GetOrder(order.orderId!))
              .pipe(takeUntil(this.onDestroy$))
              .subscribe(() => {
                if (result.receipt) {
                  this.printReceipt();
                }
              });
            this.newOrder = true;
            this.snackBar.open('Ingen betalning behövs', 'OK', {
              duration: 3000,
            });
          }
        });
    } else if (this.paymentMethod.controls['method'].value === 'Kort') {
      let dialogRef = this.dialog.open(PaxPaymentComponent, {
        width: '30vw',
        data: { order },
      });

      dialogRef
        .afterClosed()
        .pipe(takeUntil(this.onDestroy$))
        .subscribe((result) => {
          if (result && result.accepted) {
            this.snackBar.open('Order betald', 'OK', { duration: 3000 });
            if (order.amountToPay! <= result.paidAmt) {
              this.paymentFinished = true;
            }
            this.store
              .dispatch(new GetOrder(order.orderId!))
              .pipe(takeUntil(this.onDestroy$))
              .subscribe(() => {
                if (result.receipt) {
                  this.printReceipt();
                }
              });
            this.newOrder = true;
          } else if (result && result.cancelled) {
            this.snackBar.open('Order avbruten', 'OK', { duration: 3000 });
          } else if (result && result.declined) {
            this.snackBar.open('Medges ej - prova igen', 'OK', {
              duration: 10000,
            });
          } else if (result?.unavailable) {
            this.snackBar.open('Kunde inte ansluta till terminalen', 'OK', {
              duration: 10000,
            });
          }
        });
    } else if (this.paymentMethod.controls['method'].value === 'Kontant') {
      let dialogRef = this.dialog.open(CashPaymentComponent, {
        width: '30vw',
        data: { order },
      });

      dialogRef
        .afterClosed()
        .pipe(takeUntil(this.onDestroy$))
        .subscribe((result) => {
          if (result.success) {
            if (order.amountToPay! <= result.paidAmt) {
              this.paymentFinished = true;
            }
            this.store
              .dispatch(new GetOrder(order.orderId!))
              .pipe(takeUntil(this.onDestroy$))
              .subscribe(() => {
                if (result.receipt) {
                  this.printReceipt();
                }
              });
            this.newOrder = true;
            this.snackBar.open('Order betald', 'OK', { duration: 3000 });
          }
        });
    } else if (
      this.paymentMethod.controls['method'].value === 'Betala senare'
    ) {
      this.payLater(order);
    } else if (this.paymentMethod.controls['method'].value === 'Faktura') {
      this.invoiceFields?.save();

      if (!this.invoice) {
        this.invalidFormMessage = 'Ett eller flera fält är ogiltiga';
        return;
      }

      const payment: PaymentDTO = {
        paymentMethod: {
          paymentMethodId: PaymentMethodPaymentMethodIdEnum.Invoice,
        },
        paymentInvoice: this.invoice,
        transactionType: PaymentDTOTransactionTypeEnum.Payment,
        amount: this.currentOrder.amountToPay,
      };

      // Use the same method as cash, only difference is invoice object
      this.store
        .dispatch(new CheckoutOrderCash(order.orderId!, payment))
        .pipe(takeUntil(this.onDestroy$))
        .subscribe(() => {
          this.paymentFinished = true;
          this.newOrder = true;
          this.snackBar.open('Faktura sparad', 'OK', { duration: 3000 });
        });
    }
  }

  onInvoiceSave(value: PaymentInvoiceDTO) {
    this.invoice = value;
    this.invalidFormMessage = '';
  }

  payLater(order: OrderDTO) {
    this.store
      .dispatch(new CheckoutOrderPayLater(order.orderId!))
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(() => {
        this.paymentFinished = true;
        this.newOrder = true;
        this.snackBar.open('Order sparad för senare betalning', 'OK', {
          duration: 3000,
        });
      });
  }

  handleRefund() {
    this.noReset = true;
    this.paymentFinished = true;
    this.router.navigate(['aterbetalning']);
  }

  cancelOrder() {
    const confDialog = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Avbryt order',
        message: ['Är du säker på att du vill avbryta pågående order?'],
        yesBtnText: 'Ja',
        noBtnText: 'Nej',
      },
    });

    confDialog
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((result) => {
        if (result) {
          this.store.dispatch(new DeleteOrder(this.currentOrder.orderId!));
          this.router.navigate(['../hem']);
        }
      });
  }

  goToBooking() {
    const bookingId = this.getBookingId();
    if (bookingId) {
      this.store
        .dispatch(new GetBooking(bookingId))
        .pipe(takeUntil(this.onDestroy$))
        .subscribe(() => {
          this.router.navigate(['bokning']);
        });
    }
  }

  goToOrder() {
    const orderId = this.currentOrder.orderId;
    if (orderId) {
      this.store
        .dispatch(new GetOrder(orderId))
        .pipe(takeUntil(this.onDestroy$))
        .subscribe(() => {
          this.router.navigate(['order/', orderId]);
        });
    }
  }

  getBookingId(): number | undefined {
    const result = this.currentOrder.orderItems?.filter(
      (item) => item.orderItemType === 'RESOURCE'
    );
    return result?.[0].booking?.bookingId;
  }

  formatDate(date?: string | Date): string | undefined {
    return date && this.dateService.formatDate(date);
  }

  formatTime(date?: string | Date): string | undefined {
    return date && this.dateService.formatTime(date);
  }

  sendEmail() {
    const confDialog = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: `Är du säker på att du vill skicka bokningsbekräftelse till kunden/kunderna?`,
        message: [
          `Kunden/kunderna kommer att få en bokningsbekräftelse till den angivna e-postadressen och det angivna telefonnumret i bokningen.`,
        ],
        yesBtnText: 'Skicka',
      },
    });

    confDialog
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((result) => {
        if (result) {
          this.loading = true;
          const bookingItems = this.currentOrder.orderItems?.filter(
            (item) => item.orderItemType === 'RESOURCE'
          );
          bookingItems?.forEach((bookingItem) => {
            if (bookingItem.booking?.bookingId) {
              this.emailMsg = undefined;
              this.bookingApi
                .sendBookingConfirmationAdmin({
                  bookingId: bookingItem.booking.bookingId,
                })
                .pipe(takeUntil(this.onDestroy$))
                .subscribe({
                  next: () => {
                    this.emailSent = true;
                    this.emailMsg = 'Bokningsbekräftelse skickad!';
                    this.loading = false;
                  },
                  error: () => {
                    this.emailSent = false;
                    this.emailMsg =
                      'Det gick inte att skicka bokningsbekräftelsen, kontrollera e-postadressen och telefonnumret.';
                    this.loading = false;
                  },
                });
            } else {
              this.emailSent = false;
              this.emailMsg =
                'Det gick inte att skicka bokningsbekräftelsen, ladda om sidan och försök igen.';
            }
          });
        }
      });
  }

  havePrepaidTicket(order: OrderDTO): boolean {
    const prepaidTicketOrderItems = order.orderItems?.filter(
      (orderItem) =>
        orderItem.orderItemType === 'RESOURCE' &&
        orderItem.booking &&
        orderItem.booking.prepaidTicket
    );
    return prepaidTicketOrderItems!.length > 0;
  }

  hideRemoveDiscount(orderItem: OrderItemDTO) {
    return (
      orderItem.orderItemType === 'RESOURCE' && orderItem.booking?.prepaidTicket
    );
  }
}
